/**
 * Post-add-to-cart upsell -- shared core.
 *
 * `window.PostAtcUpsellCore` is an HTMLElement base class (it is NOT registered as a custom element itself).
 * It holds everything generic about the upsell body rendered by snippets/post-atc-upsell-body.liquid:
 * data JSON, colour siblings (fetch + cache), option rebuild/availability, size matching, colour
 * preselection, helper text, price/image rendering and the add-to-cart request with the `_upsell_for*`
 * line item properties. Subclasses:
 *   - <post-atc-upsell>      assets/post-add-to-cart-upsell.js (product-page popup)
 *   - <cart-drawer-upsell>   assets/cart-drawer-upsell.js (cart drawer sheet)
 *
 * Usage: a subclass calls `this.bindBody(container)` with the element that contains the body markup
 * (and may call it again with freshly inserted markup), then uses open()/close(). Hooks to override:
 * `_activate()` / `_deactivate()` (focus trap, scroll lock, key handling) and `_afterAdd(cart)`,
 * `_onItemAdded(productId)`.
 */
(function () {
  'use strict';

  // Loaded from more than one place (product popup and cart drawer): define once
  if (window.PostAtcUpsellCore) return;

  var selectors = {
    data: '[data-post-atc-data]',
    image: '[data-post-atc-upsell-image]',
    title: '[data-post-atc-upsell-title]',
    price: '[data-post-atc-upsell-price]',
    addPrice: '[data-post-atc-add-price]',
    style: '[data-post-atc-style]',
    swatch: '[data-post-atc-swatch]',
    optionSelects: 'select[data-index]',
    add: '[data-post-atc-add]',
    error: '.post-atc-upsell__error',
    helper: '[data-post-atc-helper]'
  };

  var classes = {
    open: 'is-open',
    loading: 'btn--loading'
  };

  class PostAtcUpsellCore extends HTMLElement {
    constructor() {
      super();

      this.data = {};
      this.productCache = new Map();
      this.product = null;
      this.variant = null;
      this.previousFocus = null;
      this.isAdding = false;
      // Container that holds the bound body markup
      this.root = null;
      // Size of the variant added to cart; the size select is matched against it
      this.targetSize = null;
      // Cart item the upsell is linked to via line item properties
      this.parentItem = null;
    }

    /*
      Reads the data JSON inside `root`, caches the body elements and binds their listeners. Returns false
      when the body markup/data is missing or invalid. Safe to call again with new markup (state is reset;
      listeners of the replaced markup go away with it).
    */
    bindBody(root) {
      var dataEl = root && root.querySelector(selectors.data);
      if (!dataEl) return false;

      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.warn('post-atc-upsell: invalid data JSON', error);
        return false;
      }

      this.root = root;
      this.productCache = new Map();
      this.isAdding = false;
      this.targetSize = null;
      this.parentItem = null;

      this.styleSelect = root.querySelector(selectors.style);
      this.swatch = root.querySelector(selectors.swatch);
      this.imageEl = root.querySelector(selectors.image);
      this.titleEl = root.querySelector(selectors.title);
      this.priceEl = root.querySelector(selectors.price);
      this.addPriceEl = root.querySelector(selectors.addPrice);
      this.addButton = root.querySelector(selectors.add);
      this.errorEl = root.querySelector(selectors.error);
      this.helperEl = root.querySelector(selectors.helper);

      this.product = this.data.product;
      if (this.product && this.product.handle) {
        this.productCache.set(this.product.handle, this.product);
      }

      this._readSelectedVariant();

      if (this.styleSelect) {
        this.styleSelect.addEventListener('change', () => this._onStyleChange());
      }
      this._optionSelects().forEach((select) => {
        select.addEventListener('change', () => this._onOptionChange());
      });
      if (this.addButton) {
        this.addButton.addEventListener('click', () => this._onAddClick());
      }

      return true;
    }

    /*============================================================================
      Show / hide
    ==============================================================================*/

    /*
      Colour is the part of the product title after the last "-" (same convention as
      snippets/product-color-swatches.liquid). Picks the available colour option sharing the most words with
      the added product's colour; ties keep the first option. Nothing changes when no word matches.
    */
    _preselectColor(addedTitle) {
      if (!this.styleSelect || !addedTitle) return;

      var addedWords = this._colorWords(String(addedTitle).split('-').pop());
      if (!addedWords.length) return;

      var best = null;
      var bestScore = 0;
      Array.from(this.styleSelect.options).forEach((option) => {
        if (option.dataset.available === 'false') return;

        var words = this._colorWords(option.dataset.colorName || option.textContent);
        if (!words.length) return;

        var matched = words.filter((word) => addedWords.indexOf(word) !== -1).length;
        // Most shared words first, then the share of the option's own words that matched ("Green" beats "Grey Green")
        var score = matched + matched / words.length;
        if (matched && score > bestScore) {
          best = option;
          bestScore = score;
        }
      });

      if (!best || best.selected) return;
      this.styleSelect.value = best.value;
      this._onStyleChange();
    }

    // Lowercased words of a colour name, without the "slippers" product word
    _colorWords(value) {
      return String(value)
        .toLowerCase()
        .split(/[^a-z0-9À-ɏ]+/)
        .filter((word) => word && word !== 'slippers');
    }

    open() {
      this.previousFocus = document.activeElement;
      this._clearError();

      this.classList.add(classes.open);
      this.setAttribute('aria-hidden', 'false');

      this._activate();
    }

    close(restoreFocus = true) {
      if (!this.classList.contains(classes.open)) return;

      this.classList.remove(classes.open);
      this.setAttribute('aria-hidden', 'true');

      this._deactivate();

      if (restoreFocus && this.previousFocus && this.previousFocus.focus) {
        this.previousFocus.focus();
      }
      this.previousFocus = null;
    }

    // Hooks: focus trap, scroll lock and key handling while open (set up / torn down by subclasses)
    _activate() {}

    _deactivate() {}

    /*============================================================================
      Product / colour / size selection
    ==============================================================================*/

    // Product variants carry options as option1..3 (both product JSON and .js)
    _optionValues(product, position) {
      var values = [];
      product.variants.forEach((variant) => {
        var value = variant['option' + position];
        if (value != null && values.indexOf(value) === -1) values.push(value);
      });
      return values;
    }

    // Non-colour selects (Size etc.), ordered by their option position
    _optionSelects() {
      return Array.from(this.root.querySelectorAll(selectors.optionSelects));
    }

    _selectPosition(select) {
      return parseInt(select.dataset.index.replace('option', ''), 10);
    }

    _readSelectedVariant() {
      var selects = this._optionSelects();
      if (!selects.length) {
        this.variant = this.product.variants.find((v) => v.available) || this.product.variants[0];
        return;
      }

      var match = this.product.variants.find((variant) => selects.every((select) => {
        return variant['option' + this._selectPosition(select)] === select.value;
      }));
      this.variant = match || this.product.variants.find((v) => v.available) || this.product.variants[0];
      this._updateAddButton();
    }

    _onOptionChange() {
      // Later options depend on earlier ones (same rule as the Liquid availability check)
      this._rebuildOptions();
    }

    _onStyleChange() {
      var option = this.styleSelect.selectedOptions[0];
      // Only colour-sibling options carry a product URL; a plain Style/Color option of the product has none
      if (!option || !option.dataset.url) return;

      var handle = this.styleSelect.value;
      this._setSwatch(option);

      var cached = this.productCache.get(handle);
      var request = cached
        ? Promise.resolve(cached)
        : fetch(option.dataset.url + '.js', { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error('Product request failed');
            return response.json();
          })
          .then((product) => {
            this.productCache.set(handle, product);
            return product;
          });

      this.addButton.disabled = true;
      request.then((product) => {
        // Ignore stale responses if the user picked another colour meanwhile
        if (this.styleSelect.value !== handle) return;

        this.product = product;
        // Keep the current size if the new colour has it in stock, otherwise fall back to the closest match
        this._rebuildOptions();
        this._renderProduct();
      }).catch((error) => {
        console.warn('post-atc-upsell: could not load colour', error);
        this._updateAddButton();
      });
    }

    _setSwatch(option) {
      if (!this.swatch) return;
      this.swatch.style.backgroundColor = option.dataset.swatchColor || '';
      this.swatch.style.backgroundImage = option.dataset.swatchImage
        ? 'url("' + option.dataset.swatchImage + '")'
        : '';
    }

    // Size value of the added cart item (`/cart/add.js` item has options_with_values)
    _addedSize(item) {
      var options = item.options_with_values || [];
      var sizeOption = options.find((option) => /size/i.test(option.name));
      return sizeOption ? String(sizeOption.value) : null;
    }

    _isSizeSelect(select) {
      return select.hasAttribute('data-post-atc-size') || /size/i.test(select.dataset.handle || '');
    }

    /*
      Closest available value to `target`: exact (case-insensitive) match first, then numeric distance.
      A value like "37-38" or "37/38" is treated as a range and matches any size inside it.
    */
    _closestState(states, target) {
      var available = states.filter((s) => s.available);
      var normalized = String(target).trim().toLowerCase();
      var exact = available.find((s) => String(s.value).trim().toLowerCase() === normalized);
      if (exact) return exact;

      var targetNumber = this._numbers(target)[0];
      if (targetNumber === undefined) return null;

      var best = null;
      var bestDistance = Infinity;
      available.forEach((state) => {
        var numbers = this._numbers(state.value);
        if (!numbers.length) return;

        var min = Math.min.apply(null, numbers);
        var max = Math.max.apply(null, numbers);
        var distance = targetNumber >= min && targetNumber <= max
          ? 0
          : Math.min(Math.abs(targetNumber - min), Math.abs(targetNumber - max));

        if (distance < bestDistance) {
          best = state;
          bestDistance = distance;
        }
      });
      return best;
    }

    _numbers(value) {
      return (String(value).match(/\d+(?:[.,]\d+)?/g) || []).map((n) => parseFloat(n.replace(',', '.')));
    }

    _renderHelper() {
      if (!this.helperEl || !this.targetSize || !this.helperEl.dataset.template) return;
      this.helperEl.textContent = this.helperEl.dataset.template.replace('[size]', this._helperSize(this.targetSize));
      this.helperEl.hidden = false;
    }

    /*
      Helper text only: the added size in the format picked in the page's size-conversion toggler, with the
      original (EU) value in brackets, e.g. "US MEN 8 (EU 38)". Falls back to the plain value.
    */
    _helperSize(size) {
      var conversion = this.data.sizeConversion || {};
      var formats = conversion.formats || {};
      var format = this._currentSizeFormat(formats);
      if (!format || format === 'eu') return size;

      var entry = (conversion.entries || {})[size];
      var converted = entry && entry[format];
      if (!converted) return size;

      return formats[format] + ' ' + converted + ' (' + formats.eu + ' ' + size + ')';
    }

    // Format key of the active size-conversion tab; theme.js stores it in localStorage on every switch
    _currentSizeFormat(formats) {
      var label = null;
      try {
        label = localStorage.getItem('theme-size-tab');
      } catch (error) {
        label = null;
      }

      if (!label) {
        var active = document.querySelector('.product-section .size-conversion__toggler .js-toggle-size-conversion.active');
        label = active ? active.dataset.countryToggler : null;
      }
      if (!label) return null;

      var normalized = String(label).trim().toLowerCase();
      return Object.keys(formats).find((key) => String(formats[key]).trim().toLowerCase() === normalized) || null;
    }

    /*
      Rebuilds the option selects from `this.product`. Availability: a value is sold out when no available
      variant has it; option2/option3 must also match the earlier selected values.
      Selection per select: the current value if still available (unless `preferTarget`), then -- for the
      size select -- the value closest to the added size, then the first available value.
    */
    _rebuildOptions(preferTarget) {
      var selects = this._optionSelects().sort((a, b) => this._selectPosition(a) - this._selectPosition(b));
      var chosen = [];

      selects.forEach((select) => {
        var position = this._selectPosition(select);
        var values = this._optionValues(this.product, position);
        var previous = select.value;

        var states = values.map((value) => {
          var available = this.product.variants.some((variant) => {
            if (variant['option' + position] !== value) return false;
            for (var i = 1; i < position; i++) {
              if (variant['option' + i] !== chosen[i - 1]) return false;
            }
            return variant.available;
          });
          return { value: value, available: available };
        });

        var kept = preferTarget ? null : states.find((s) => s.value === previous && s.available);
        var closest = this.targetSize && this._isSizeSelect(select) ? this._closestState(states, this.targetSize) : null;
        var selected = kept || closest || states.find((s) => s.available) || states[0];

        select.innerHTML = '';
        states.forEach((state) => {
          var option = document.createElement('option');
          option.value = state.value;
          option.dataset.index = 'option' + position;
          option.textContent = state.value;
          if (!state.available) {
            option.disabled = true;
            option.dataset.soldOut = '';
            option.textContent += ' — ' + this.data.soldOutText;
          }
          option.selected = !!selected && state.value === selected.value;
          select.appendChild(option);
        });

        chosen.push(selected ? selected.value : undefined);
      });

      this._readSelectedVariant();
      this._renderPrice();
    }

    /*============================================================================
      Rendering
    ==============================================================================*/

    _formatPrice(cents) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }

    _renderPrice() {
      if (!this.variant) return;
      var price = this._formatPrice(this.variant.price);
      if (this.priceEl) this.priceEl.textContent = price;
      if (this.addPriceEl) this.addPriceEl.textContent = price;
    }

    // Title + image follow the product (colour); price follows the variant
    _renderProduct() {
      if (this.titleEl) this.titleEl.textContent = this.product.title;
      this._renderPrice();
      this._renderImage();
      this._updateAddButton();
    }

    _renderImage() {
      if (!this.imageEl) return;

      var src = this._imageUrl(this.product.featured_image);
      if (!src) return;

      var url;
      try {
        url = new URL(src, window.location.origin);
        url.searchParams.set('width', '128');
        src = url.toString();
      } catch (error) {
        // Keep the original URL if it cannot be parsed
      }

      var img = document.createElement('img');
      img.src = src;
      img.alt = (this.product.featured_image && this.product.featured_image.alt) || this.product.title;
      img.width = 64;
      img.height = 62;
      img.loading = 'lazy';
      this.imageEl.replaceChildren(img);
    }

    _imageUrl(image) {
      if (!image) return '';
      return typeof image === 'string' ? image : image.src || image.url || '';
    }

    _updateAddButton() {
      if (!this.addButton) return;
      var available = !!(this.variant && this.variant.available);
      this.addButton.disabled = !available || this.isAdding;
    }

    /*============================================================================
      Add to cart
    ==============================================================================*/

    _onAddClick() {
      if (this.isAdding || !this.variant || !this.variant.available) return;

      this.isAdding = true;
      this._clearError();
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.classList.add(classes.loading);
      this.addButton.disabled = true;

      var addedProductId = this.product.id;

      fetch(theme.routes.cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: this.variant.id, quantity: 1, properties: this._parentProperties() }]
        })
      })
        .then((response) => response.json().then((json) => ({ ok: response.ok, json: json })))
        .then((result) => {
          if (!result.ok || result.json.status) {
            throw new Error(result.json.description || result.json.message || this.data.errorText);
          }
          this._onItemAdded(addedProductId);
          return fetch(theme.routes.cart, { credentials: 'same-origin' }).then((r) => r.json());
        })
        .then((cart) => this._afterAdd(cart))
        .catch((error) => {
          this._showError(error && error.message ? error.message : this.data.errorText);
        })
        .finally(() => {
          this.isAdding = false;
          this.addButton.removeAttribute('aria-busy');
          this.addButton.classList.remove(classes.loading);
          this._updateAddButton();
        });
    }

    // Hook: the upsell variant was added to the cart (before the fresh cart is fetched)
    _onItemAdded(productId) {}

    /*
      Hook: runs with the fresh cart after a successful add. Default: refreshes the cart count and shipping
      bar (`cart:updated`), re-renders the drawer (`cart:build`) and closes without restoring focus.
    */
    _afterAdd(cart) {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      document.dispatchEvent(new CustomEvent('cart:build'));

      this.close(false);
    }

    /*
      Line item properties that tie the upsell to the product added on the product page.
      Keys start with "_" so cart-item.liquid (and checkout) hide them; they stay visible on the order in admin.
    */
    _parentProperties() {
      var parent = this.parentItem;
      if (!parent) return {};

      var name = parent.product_title || '';
      if (parent.variant_title) name += ' - ' + parent.variant_title;

      return {
        _upsell_for: name,
        _upsell_for_variant_id: String(parent.variant_id || parent.id || ''),
        _upsell_for_key: parent.key || ''
      };
    }

    _showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    _clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  window.PostAtcUpsellCore = PostAtcUpsellCore;
})();
